<template>
  <div class="text-xs">
    <LoadingIndicator v-if="loading" message="Loading patient..." />

    <div v-else-if="error" class="text-red-600">
      {{ error }}
      <button type="button" class="underline ml-1" @click="load">Retry</button>
    </div>

    <template v-else-if="summary">
      <!-- IDENTITY HEADER -->
      <div class="border border-gray-200 rounded mb-4">
        <div class="px-4 py-3 border-b border-gray-200 flex items-start justify-between">
          <div>
            <div class="text-sm font-medium text-gray-900">{{ displayName }}</div>
            <div class="text-gray-500 mt-0.5">
              {{ summary.DOB || 'DOB unknown' }}
              <span v-if="summary.SEX"> · {{ sexLabel(summary.SEX) }}</span>
              <span v-if="summary.STATE"> · {{ summary.STATE }}{{ summary.ZIP ? ' ' + summary.ZIP : '' }}</span>
            </div>
          </div>
          <button
            type="button"
            class="text-blue-600 hover:underline"
            @click="$emit('back')"
          >&larr; Back to search</button>
        </div>

        <div class="grid grid-cols-5 divide-x divide-gray-200">
          <div v-for="s in stats" :key="s.label" class="px-4 py-2.5">
            <div class="text-gray-500 uppercase text-[10px]">{{ s.label }}</div>
            <div
              class="text-sm mt-0.5"
              :class="s.warn ? 'text-amber-700 font-medium' : 'text-gray-900'"
            >{{ s.value }}</div>
          </div>
        </div>

        <!-- A patient with several MBIs is NORMAL (CMS reissues them). It only
             matters alongside disagreeing names or DOBs below, so say that
             rather than flagging it as a problem on its own. -->
        <div
          v-if="summary.N_MBIS > 1"
          class="px-4 py-2 bg-amber-50 border-t border-amber-200 text-amber-800"
        >
          {{ summary.N_MBIS }} MBIs on this patient. Usually CMS reissuance and
          correct — but if the Identity rows below disagree on name or DOB, this
          is a false merge.
        </div>
      </div>

      <!-- CONTRACTS -->
      <div v-if="contracts.length" class="mb-4">
        <div class="text-gray-500 uppercase text-[10px] mb-1.5">
          Contracts ({{ contracts.length }})
        </div>
        <table class="w-full border border-gray-200 rounded">
          <colgroup>
            <col style="width: 26%" /><col style="width: 12%" />
            <col style="width: 12%" /><col style="width: 20%" />
            <col style="width: 15%" /><col style="width: 15%" />
          </colgroup>
          <thead>
            <tr class="text-left font-medium uppercase text-gray-500 border-b border-gray-200">
              <th class="py-1.5 pl-3 pr-2">Contract</th>
              <th class="py-1.5 px-2">Payer</th>
              <th class="py-1.5 px-2">LOB</th>
              <th class="py-1.5 px-2">Period</th>
              <th class="py-1.5 px-2">Latest report</th>
              <th class="py-1.5 px-2 pr-3">Source</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="c in contracts" :key="c.COHORT_MASTER" class="border-b border-gray-50">
              <td class="py-1.5 pl-3 pr-2 align-middle">
                {{ c.CONTRACT_ID }}
                <!-- One cohort answers to several labels across feeds; show the
                     others so a search on any of them makes sense. -->
                <span v-if="otherLabels(c).length" class="text-gray-400">
                  ({{ otherLabels(c).join(', ') }})
                </span>
              </td>
              <td class="py-1.5 px-2 align-middle text-gray-600">{{ c.PAYER_ID || '—' }}</td>
              <td class="py-1.5 px-2 align-middle text-gray-600">{{ c.LOB || '—' }}</td>
              <td class="py-1.5 px-2 align-middle text-gray-600">
                {{ c.VALID_FROM || '—' }} &ndash; {{ c.VALID_THRU || '—' }}
              </td>
              <td class="py-1.5 px-2 align-middle text-gray-600">{{ c.LATEST_REPORT_DATE || '—' }}</td>
              <td class="py-1.5 px-2 pr-3 align-middle text-gray-500">
                {{ asArray(c.SOURCES).join(', ') }}
                <span v-if="c.SOURCE_RELIABILITY != null" class="text-gray-400">
                  (rel {{ c.SOURCE_RELIABILITY }})
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- EVIDENCE. The point of the screen: every asserted value with the feed
           that said it, winners and losers together. -->
      <div class="text-gray-500 uppercase text-[10px] mb-1.5">
        Evidence ({{ evidence.length }} assertions)
      </div>
      <div class="flex items-center gap-3 mb-2">
        <label
          v-for="d in domains"
          :key="d"
          class="flex items-center gap-1 cursor-pointer"
        >
          <input v-model="visibleDomains" type="checkbox" :value="d" class="cursor-pointer" />
          <span>{{ d }}</span>
        </label>
        <label class="flex items-center gap-1 cursor-pointer ml-2">
          <input v-model="winnersOnly" type="checkbox" class="cursor-pointer" />
          <span>Winners only</span>
        </label>
      </div>

      <div class="border border-gray-200 rounded overflow-auto max-h-[520px]">
        <table class="w-full">
          <colgroup>
            <col style="width: 11%" /><col style="width: 14%" />
            <col style="width: 24%" /><col style="width: 19%" />
            <col style="width: 7%" /><col style="width: 8%" />
            <col style="width: 9%" /><col style="width: 8%" />
          </colgroup>
          <thead class="sticky top-0 bg-white shadow-sm">
            <tr class="text-left font-medium uppercase text-gray-500 border-b border-gray-200">
              <th class="py-2 pl-3 pr-2">Domain</th>
              <th class="py-2 px-2">Feature</th>
              <th class="py-2 px-2">Value</th>
              <th class="py-2 px-2">Feed</th>
              <th class="py-2 px-2 text-right">Rel</th>
              <th class="py-2 px-2 text-right">Records</th>
              <th class="py-2 px-2">Last report</th>
              <th class="py-2 px-2 pr-3">Flags</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(e, i) in filteredEvidence"
              :key="i"
              class="border-b border-gray-50"
              :class="e.IS_WINNER ? '' : 'text-gray-400'"
            >
              <td class="py-1.5 pl-3 pr-2 align-middle">{{ e.DOMAIN }}</td>
              <td class="py-1.5 px-2 align-middle">{{ e.FEATURE }}</td>
              <td class="py-1.5 px-2 align-middle" :class="e.IS_WINNER ? 'font-medium' : ''">
                {{ e.VALUE || '—' }}
              </td>
              <td class="py-1.5 px-2 align-middle">{{ e.SOURCE }}</td>
              <td class="py-1.5 px-2 align-middle text-right">
                {{ e.SOURCE_RELIABILITY != null ? e.SOURCE_RELIABILITY : '—' }}
              </td>
              <td class="py-1.5 px-2 align-middle text-right">{{ fmt(e.RECORD_COUNT) }}</td>
              <td class="py-1.5 px-2 align-middle">{{ e.LAST_REPORT_DATE || '—' }}</td>
              <td class="py-1.5 px-2 pr-3 align-middle">
                <span v-if="e.IS_WINNER" class="text-green-700" title="This value won for this feature">✓</span>
                <span v-if="e.IS_PRIMARY_SOURCE" class="text-blue-700 ml-1" title="Authoritative feed for this value">P</span>
                <span v-if="e.VERIFIED" class="text-gray-700 ml-1" title="Issuer-direct, not relayed">V</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="text-gray-500 mt-2">
        <span class="text-green-700">✓</span> winning value ·
        <span class="text-blue-700">P</span> authoritative feed for that value ·
        <span class="text-gray-700">V</span> issuer-direct.
        A row can be the winning value from a non-authoritative feed, or the
        reverse — the two flags are independent.
      </div>
    </template>
  </div>
</template>

<script>
import LoadingIndicator from '@/components/widgetcomponents/library/LoadingIndicator.vue';
import { getPatientSummary, getPatientEvidence } from '@/services/api/mdr/read';

export default {
  name: 'MDRPatient',
  components: { LoadingIndicator },
  props: {
    patientMaster: { type: String, required: true },
  },
  emits: ['back'],
  data() {
    return {
      loading: false,
      error: null,
      summary: null,
      contracts: [],
      evidence: [],
      visibleDomains: [],
      winnersOnly: false,
    };
  },
  computed: {
    displayName() {
      if (!this.summary) return '';
      const parts = [this.summary.FIRST_NAME, this.summary.MIDDLE_NAME, this.summary.LAST_NAME]
        .filter(Boolean);
      return parts.length ? parts.join(' ') : '(no name on any feed)';
    },
    domains() {
      return [...new Set(this.evidence.map((e) => e.DOMAIN))].sort();
    },
    filteredEvidence() {
      return this.evidence.filter((e) => (
        this.visibleDomains.includes(e.DOMAIN)
        && (!this.winnersOnly || e.IS_WINNER)
      ));
    },
    stats() {
      const s = this.summary || {};
      return [
        { label: 'Identifiers', value: this.fmt(s.N_IDS) },
        { label: 'ID types', value: this.fmt(s.N_ID_TYPES) },
        { label: 'MBIs', value: this.fmt(s.N_MBIS), warn: s.N_MBIS > 1 },
        { label: 'Feeds', value: this.fmt(s.N_SOURCES) },
        { label: 'Contracts', value: this.fmt(s.N_CONTRACTS) },
      ];
    },
  },
  watch: {
    patientMaster: 'load',
  },
  mounted() {
    this.load();
  },
  methods: {
    fmt(n) {
      return n == null ? '—' : Number(n).toLocaleString();
    },
    sexLabel(s) {
      return { 1: 'Male', 2: 'Female' }[String(s)] || s;
    },
    asArray(v) {
      if (Array.isArray(v)) return v;
      if (typeof v === 'string') {
        try { return JSON.parse(v); } catch { return [v]; }
      }
      return [];
    },
    otherLabels(c) {
      return this.asArray(c.ALL_LABELS).filter((l) => l !== c.CONTRACT_ID);
    },
    async load() {
      this.loading = true;
      this.error = null;
      try {
        const [summaryRes, evidenceRes] = await Promise.all([
          getPatientSummary(this.patientMaster),
          getPatientEvidence(this.patientMaster),
        ]);
        this.summary = summaryRes.summary;
        this.contracts = summaryRes.contracts || [];
        this.evidence = evidenceRes || [];
        // Default every domain on: hiding one by default means someone
        // investigating a merge never sees the rows that explain it.
        this.visibleDomains = [...new Set(this.evidence.map((e) => e.DOMAIN))];
      } catch (e) {
        this.error = e?.response?.status === 404
          ? 'Patient not found in the published run.'
          : "Couldn't load this patient.";
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>
